import type { IDealGroup } from '@/Interfaces/Beeswax/DealGroup.interface';
import type { IClient } from '@/Interfaces/Client.interface';
import type { IClientControlOption } from '@/Interfaces/ControlOption.interface';
import type { ICreativeSize } from '@/Interfaces/Creatives/CreativeSize.interface';
import type { IPackage } from '@/Interfaces/Packages/Package.interface';
import type { IExtendedSignalThunkArg } from '@/Interfaces/Slices/AsyncThunk.interface';
import type { IWhitelabelWithRelations } from '@/Interfaces/Whitelabels/Whitelabel.interface';
import { DEFAULT_LOADING_REQUEST_ID } from '@/constants/slice';
import { fetchClients } from '@/helpers/axios';
import { isRoleAllowedWhitelabelsEndpoint } from '@/helpers/roles';
import type { RootState } from '@/store';
import { fetchWhitelabels } from '@/store/api/whitelabels.api';
import { fulfilledCaseHandler, pendingCaseHandler, rejectedCaseHandler } from '@/store/asyncThunkHandlers';
import type { RequestMap } from '@/types/RequestMap.types';
import type { RequestId } from '@/types/Slice.type';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { fetchCreativeSizes } from '../api/creativeSizes.api';
import { fetchDealGroups } from '../api/deals.api';
import { fetchPackages } from '../api/packages.api';

enum Action {
  FetchClients = 'sharedApi/fetchClients',
  FetchWhitelabels = 'sharedApi/fetchWhitelabels',
  FetchDealGroups = 'sharedApi/fetchDealGroups',
  FetchPackages = 'sharedApi/fetchPackages',
  FetchCreativeSizes = 'sharedApi/fetchCreativeSizes',
}

interface ISharedApiState {
  clients: IClient[];
  whitelabels: IWhitelabelWithRelations[];
  clientsRequestId: string | null;
  whitelabelsRequestId: string | null;
  dealGroups: IDealGroup[];
  dealGroupsRequestId: RequestId;
  packages: IPackage[];
  packagesRequestId: RequestId;
  creativeSizes: ICreativeSize[];
  creativeSizesRequestId: RequestId;
}

const initialState: ISharedApiState = {
  clients: [],
  whitelabels: [],
  clientsRequestId: DEFAULT_LOADING_REQUEST_ID,
  whitelabelsRequestId: DEFAULT_LOADING_REQUEST_ID,
  dealGroups: [],
  dealGroupsRequestId: DEFAULT_LOADING_REQUEST_ID,
  packages: [],
  packagesRequestId: DEFAULT_LOADING_REQUEST_ID,
  creativeSizes: [],
  creativeSizesRequestId: DEFAULT_LOADING_REQUEST_ID,
};

export const getClients = createAsyncThunk<IClientControlOption[], IExtendedSignalThunkArg | undefined>(
  Action.FetchClients,
  async _ => {
    return await fetchClients(null);
  },
  {
    condition(payload, { getState }) {
      const {
        sharedApi: { clients },
        auth: { isImpersonationMode },
      } = getState() as RootState;
      return payload?.refresh || !clients.length || isImpersonationMode;
    },
  },
);

export const getWhitelabels = createAsyncThunk<IWhitelabelWithRelations[], IExtendedSignalThunkArg | undefined>(
  Action.FetchWhitelabels,
  async _ => {
    const response = await fetchWhitelabels({ loadRelations: true, page: 1, limit: 1000 });
    return response.data as IWhitelabelWithRelations[];
  },
  {
    condition(payload, { getState }) {
      const {
        sharedApi: { whitelabels },
        me: { role },
        auth: { isImpersonationMode },
      } = getState() as RootState;

      return isRoleAllowedWhitelabelsEndpoint(role) && (payload?.refresh || !whitelabels.length || isImpersonationMode);
    },
  },
);

export const getDealGroups = createAsyncThunk<IDealGroup[], IExtendedSignalThunkArg | undefined>(
  Action.FetchDealGroups,
  async () => {
    return await fetchDealGroups();
  },
  {
    condition: (payload, { getState }) => {
      const {
        sharedApi: { dealGroups },
      } = getState() as RootState;

      if (payload?.refresh) return true;
      return !dealGroups.length;
    },
  },
);

export const getPackages = createAsyncThunk<IPackage[], IExtendedSignalThunkArg | undefined>(
  Action.FetchPackages,
  async () => {
    return await fetchPackages();
  },
  {
    condition: (payload, { getState }) => {
      const {
        sharedApi: { packages },
      } = getState() as RootState;

      if (payload?.refresh) return true;
      return !packages.length;
    },
  },
);

export const getCreativeSizes = createAsyncThunk<ICreativeSize[], IExtendedSignalThunkArg | undefined>(
  Action.FetchCreativeSizes,
  async () => {
    return await fetchCreativeSizes();
  },
  {
    condition: (payload, { getState }) => {
      const {
        sharedApi: { creativeSizes },
      } = getState() as RootState;

      if (payload?.refresh) return true;
      return !creativeSizes.length;
    },
  },
);

export const requestMap: RequestMap<ISharedApiState> = [
  ['whitelabels', getWhitelabels],
  ['clients', getClients],
  ['dealGroups', getDealGroups],
  ['packages', getPackages],
  ['creativeSizes', getCreativeSizes],
];

export const sharedApiSlice = createSlice({
  name: 'sharedApi',
  initialState,
  reducers: {},
  extraReducers: builder => {
    requestMap.forEach(([key, thunk]) => {
      builder
        .addCase(thunk.pending, pendingCaseHandler(key))
        .addCase(thunk.rejected, rejectedCaseHandler(key))
        .addCase(thunk.fulfilled, fulfilledCaseHandler(key));
    });
  },
});

export default sharedApiSlice.reducer;
